import React from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { TableCell, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { classificationKeys, methodKeys } from '@utils/verificationLabels';
import type { VerificationItem, VerificationUnit } from '../../../types/verification';
import UnitActionsMenu, { type UnitActionsMenuProps } from './UnitActionsMenu';
import VerificationItemActions from './VerificationItemActions';
import { dataCell, markCell, specCell, stickyCol0, stickyCol1, unitCell } from './itemTableLayout';

export interface VerificationItemRowProps
  extends Omit<UnitActionsMenuProps, 'unit'> {
  unit: VerificationUnit;
  item: VerificationItem;
  spec: string;
  /** Rendered on the unit's first row only, spanning every row it owns. */
  isFirstInUnit: boolean;
  unitRowSpan: number;
  /** Rendered on the spec group's first row only. */
  isFirstInGroup: boolean;
  specRowSpan: number;
  onEditItem?: (item: VerificationItem) => void;
  onDeleteItem?: (itemId: string) => void;
}

const VerificationItemRow: React.FC<VerificationItemRowProps> = ({
  unit,
  item,
  spec,
  isFirstInUnit,
  unitRowSpan,
  isFirstInGroup,
  specRowSpan,
  onAddItem,
  onEditUnit,
  onDeleteUnit,
  onEditItem,
  onDeleteItem,
}) => {
  const { t } = useTranslation();

  return (
    <TableRow>
      {isFirstInUnit && (
        <TableCell rowSpan={unitRowSpan} className={cn(unitCell, stickyCol0, 'revy-figure text-center')}>
          {unit.unitNumber}
        </TableCell>
      )}
      {isFirstInUnit && (
        <TableCell rowSpan={unitRowSpan} className={cn(unitCell, stickyCol1)}>
          <div className="flex items-start gap-0.5">
            <p className="revy-cjk flex-1 text-xs font-bold leading-[1.5]">{unit.unitName}</p>
            <div className="flex flex-col gap-0.5">
              <UnitActionsMenu
                unit={unit}
                onAddItem={onAddItem}
                onEditUnit={onEditUnit}
                onDeleteUnit={onDeleteUnit}
              />
            </div>
          </div>
        </TableCell>
      )}
      {isFirstInGroup && (
        <TableCell rowSpan={specRowSpan} className={specCell}>
          <p className="revy-cjk text-xs leading-[1.5]">{spec}</p>
        </TableCell>
      )}
      <TableCell className={dataCell}>
        <p className="revy-cjk text-xs leading-[1.5]">{item.itemName}</p>
      </TableCell>
      {classificationKeys.map((key) => (
        <TableCell key={key} className={markCell}>
          {item.classifications.includes(key) && <span className="text-xs">◎</span>}
        </TableCell>
      ))}
      <TableCell className={dataCell}>
        <p className="revy-cjk whitespace-pre-wrap text-[0.8rem] leading-[1.5]">
          {item.standardTarget}
        </p>
      </TableCell>
      {methodKeys.map((key) => (
        <TableCell key={key} className={markCell}>
          {item.verificationMethodsPrimary.includes(key) && <span className="text-xs">■</span>}
          {item.verificationMethodsSecondary.includes(key) && <span className="text-xs">□</span>}
        </TableCell>
      ))}
      <TableCell className={cn(dataCell, 'text-center')}>
        {item.reviewJudgment === 'required' && (
          <Badge variant="destructive" className="h-5 px-1.5 text-[0.7rem] leading-[1.5]">
            {t('verification.reviewRequired')}
          </Badge>
        )}
        {item.reviewJudgment === 'not_required' && (
          <span className="revy-cjk text-[0.8rem] leading-[1.5]">
            {t('verification.reviewNotRequired')}
          </span>
        )}
      </TableCell>
      <TableCell className={cn(dataCell, 'px-0')}>
        <VerificationItemActions
          item={item}
          onEditItem={onEditItem}
          onDeleteItem={onDeleteItem}
        />
      </TableCell>
    </TableRow>
  );
};

export default VerificationItemRow;
